// Multi-language content for the Alvin Kumar & Associates landing page.
// Proper nouns (Fiji, Nadi, Suva, TIN, VAT, MCTTT, WeChat, WhatsApp, Alvin_Fiji) stay untranslated.

export const LANGS = [
  { code: "en", label: "English" },
  { code: "zh", label: "中文" },
  { code: "hi", label: "हिन्दी" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
];

export const RTL_LANGS = ["ar"];

export const translations = {
  en: {
    nav: { getGuide: "Get the Free Guide" },
    hero: {
      badge: "For foreign investors · Nadi & Suva",
      line1: "Set Up Your Business",
      line2: "in Fiji — Almost",
      line3: "Entirely Remote",
      sub: "The only step that requires you to be physically in Fiji is opening your bank account. Everything else — company registration, tax, immigration permits — can be done from anywhere.",
      cta: "Get the Free Guide",
      ctaNote: "Free step-by-step guide · No obligation",
      caption: "Register remotely. Bank in person. We handle both.",
    },
    why: {
      overline: "Why this matters",
      title: "The process is confusing on purpose. It doesn't have to be.",
      body: "Unclear costs. Conflicting advice on what needs travel. The fear of registering, then stalling for weeks. Most investors give up before they start — not because Fiji is hard, but because no one lays out the full path in one place.",
      points: [
        { title: "A registered advisory firm", body: "We are a registered advisory firm — not a directory or a lead broker. You work directly with the people handling your setup." },
        { title: "Offices in Nadi and Suva", body: "On-the-ground presence in both major hubs means the one step that needs a physical presence — your bank account — is handled locally." },
        { title: "Registration through to banking", body: "We handle the whole chain end-to-end: company registration, tax, permits and the corporate bank account. One team, one path." },
        { title: "Foreign investors, every industry", body: "We work with foreign investors across all industries — tourism, trade, services, manufacturing — not just one narrow sector." },
      ],
    },
    process: {
      overline: "The process",
      title: "Eight steps to set up a company in Fiji.",
      sub: "Here are the stages and realistic timelines. The full fees, documentation checklists and insider tips are in the free guide.",
      note: "Full fees, documentation checklists, and the insider tips we don't put in writing anywhere else — get them in the free guide below.",
      cta: "Send Me the Free Guide",
      steps: [
        { title: "Company Registration", time: "2–3 working days" },
        { title: "Shareholder & Director TIN Registration", time: "2–3 working days" },
        { title: "Company Tax & VAT Registration", time: "1 working day" },
        { title: "Business Profile & MCTTT Registration", time: "1–3 working days" },
        { title: "Reserve Bank of Fiji Exchange Control Approval", time: "~3 working days" },
        { title: "Investor Immigration Permits", time: "~21 working days" },
        { title: "Work Permit Applications", time: "Varies" },
        { title: "Business Bank Account Setup", time: "The only in-person step" },
      ],
    },
    marquee: ["Start a business in Fiji", "Foreign company registration", "Fiji investor permit", "Set up a company remotely", "Exchange control approval", "Corporate banking"],
    form: {
      overline: "The free guide",
      title: "How to Set Up a Business in Fiji",
      sub: "The complete step-by-step guide — plus the insider tips nobody tells you about. Fees, document checklists, timelines and the traps to avoid. Delivered straight to your inbox.",
      name: "Full Name", namePh: "Jane Doe",
      email: "Email Address", emailPh: "jane@company.com",
      phone: "Phone Number (with country code)", phonePh: "+1 555 123 4567",
      submit: "Send Me the Free Guide", sending: "Sending…",
      reassurance: "No spam. We'll only reach out about your business setup in Fiji.",
      successTitle: "Check your inbox.",
      successPre: "Your free guide is on its way to",
      successPost: "as a PDF. If you don't see it in a few minutes, check your spam folder. We'll only reach out about your business setup in Fiji.",
      errName: "Please enter your full name.",
      errEmail: "Please enter a valid email address.",
      errPhone: "Please enter your phone number with country code.",
      errorToast: "Something went wrong sending your guide. Please try again.",
    },
    faq: {
      overline: "FAQ",
      title: "Frequently asked questions",
      sub: "Straight answers on remote setup, timelines, local directors and investment requirements.",
      items: [
        { q: "Can I set up a business in Fiji without traveling there?", a: "Yes, every step except opening the corporate bank account can be completed remotely, including company registration, tax registration, exchange control approval, and lodging immigration permit applications." },
        { q: "How long does it take to register a company in Fiji?", a: "Company registration itself typically takes 2-3 working days; the full process including permits and banking can take several weeks depending on the investor immigration permit timeline (around 21 working days)." },
        { q: "Do foreign investors need a local director in Fiji?", a: "Fijian company law requires a local director; this can be satisfied by appointing someone you know locally or by using a local director service." },
        { q: "Is there a minimum investment requirement for foreign-owned companies in Fiji?", a: "There is a statutory minimum investment requirement for foreign-owned companies, though a waiver can sometimes be applied for depending on the nature of the business." },
        { q: "What documents do I need to register a company in Fiji as a foreigner?", a: "Certified copies of passport or driver's licence and birth certificate are required for all shareholders and directors, with additional documents (police clearance, medical report, business plan, financials) required for investor permit applications." },
      ],
    },
    locations: { overline: "Our offices", title: "Find us in Nadi & Suva", nadi: "Nadi Office", suva: "Suva Office", directions: "Get directions" },
    footer: {
      blurb: "Helping foreign investors start a business in Fiji — company registration through to banking, handled end-to-end from our offices in Nadi and Suva.",
      cta: "Get the Free Guide",
      getInTouch: "Get in touch",
      whatsapp: "WhatsApp us",
      wechat: "WeChat: Alvin_Fiji",
      booking: "Book a consultation",
      offices: "Offices in Nadi & Suva, Fiji",
      smallprint: "Alvin Kumar & Associates — Registered advisory firm serving Nadi & Suva.",
      rights: "All rights reserved.",
    },
  },

  zh: {
    nav: { getGuide: "获取免费指南" },
    hero: {
      badge: "面向外国投资者 · 楠迪与苏瓦",
      line1: "在斐济",
      line2: "开办您的企业 —",
      line3: "几乎全程远程办理",
      sub: "唯一需要您亲自到斐济的环节是开设银行账户。其余所有事项——公司注册、税务、移民许可——都可以在任何地方完成。",
      cta: "获取免费指南",
      ctaNote: "免费分步指南 · 无任何义务",
      caption: "远程注册，亲临开户，两者我们全程代办。",
    },
    why: {
      overline: "为什么这很重要",
      title: "这个流程被刻意弄得复杂。其实它不必如此。",
      body: "费用不透明、关于哪些环节需要出行的说法互相矛盾、担心注册后却拖延数周。许多投资者还没开始就放弃了——不是因为斐济难,而是因为没有人把完整的路径清晰地摆在一处。",
      points: [
        { title: "持牌顾问公司", body: "我们是一家持牌顾问公司——不是名录网站,也不是中介。您将直接与负责您业务办理的人员合作。" },
        { title: "楠迪与苏瓦均设办公室", body: "在两大枢纽城市的本地驻点意味着,唯一需要亲自到场的环节——开设银行账户——由我们在当地为您办理。" },
        { title: "从注册到开户全程代办", body: "我们端到端处理整条链路:公司注册、税务、各类许可以及企业银行账户。一支团队,一条路径。" },
        { title: "服务各行业外国投资者", body: "我们服务各行各业的外国投资者——旅游、贸易、服务、制造——而不局限于某一个狭窄领域。" },
      ],
    },
    process: {
      overline: "办理流程",
      title: "在斐济开办公司的八个步骤。",
      sub: "以下是各阶段及实际所需时间。完整费用、文件清单和内部经验都在免费指南中。",
      note: "完整费用、文件核对清单,以及我们从不落于书面的内部经验——都在下方的免费指南中获取。",
      cta: "把免费指南发给我",
      steps: [
        { title: "公司注册", time: "2–3 个工作日" },
        { title: "股东与董事 TIN 税号注册", time: "2–3 个工作日" },
        { title: "公司税务与 VAT 增值税注册", time: "1 个工作日" },
        { title: "企业档案与 MCTTT 注册", time: "1–3 个工作日" },
        { title: "斐济储备银行外汇管制审批", time: "约 3 个工作日" },
        { title: "投资者移民许可", time: "约 21 个工作日" },
        { title: "工作许可申请", time: "视情况而定" },
        { title: "企业银行账户开设", time: "唯一需亲自到场的环节" },
      ],
    },
    marquee: ["在斐济开办企业", "外资公司注册", "斐济投资者许可", "远程注册公司", "外汇管制审批", "企业银行业务"],
    form: {
      overline: "免费指南",
      title: "如何在斐济开办企业",
      sub: "完整的分步指南——外加无人愿意告诉你的内部经验。费用、文件清单、时间表以及需要避开的陷阱,直接发送至您的邮箱。",
      name: "全名", namePh: "张三",
      email: "电子邮箱", emailPh: "you@company.com",
      phone: "电话号码(含国家区号)", phonePh: "+86 138 0000 0000",
      submit: "把免费指南发给我", sending: "发送中…",
      reassurance: "无垃圾邮件。我们只会就您在斐济的企业设立事宜与您联系。",
      successTitle: "请查收您的邮箱。",
      successPre: "您的免费指南(PDF)正发送至",
      successPost: "。若几分钟内未收到,请查看垃圾邮件文件夹。我们只会就您在斐济的企业设立事宜与您联系。",
      errName: "请输入您的全名。",
      errEmail: "请输入有效的电子邮箱地址。",
      errPhone: "请输入含国家区号的电话号码。",
      errorToast: "发送指南时出错,请重试。",
    },
    faq: {
      overline: "常见问题",
      title: "常见问题解答",
      sub: "关于远程设立、办理时间、本地董事及投资要求的直接解答。",
      items: [
        { q: "我可以不亲自前往斐济就开办企业吗?", a: "可以,除开设公司银行账户外,其余所有环节都可以远程完成,包括公司注册、税务注册、外汇管制审批以及递交移民许可申请。" },
        { q: "在斐济注册公司需要多长时间?", a: "公司注册本身通常需要 2–3 个工作日;包含许可和开户在内的完整流程可能需要数周,具体取决于投资者移民许可的时间(约 21 个工作日)。" },
        { q: "外国投资者在斐济是否需要本地董事?", a: "斐济公司法要求设有一名本地董事;可通过委任您在当地认识的人,或使用本地董事服务来满足此项要求。" },
        { q: "外资公司在斐济是否有最低投资额要求?", a: "外资公司有法定的最低投资额要求,但视业务性质而定,有时可申请豁免。" },
        { q: "作为外国人在斐济注册公司需要哪些文件?", a: "所有股东和董事均需提供护照或驾照及出生证明的公证副本;投资者许可申请还需额外文件(无犯罪记录证明、体检报告、商业计划书、财务资料)。" },
      ],
    },
    locations: { overline: "我们的办公室", title: "在楠迪与苏瓦找到我们", nadi: "楠迪办公室", suva: "苏瓦办公室", directions: "获取路线" },
    footer: {
      blurb: "协助外国投资者在斐济开办企业——从公司注册到银行开户,由我们楠迪和苏瓦的办公室端到端全程办理。",
      cta: "获取免费指南",
      getInTouch: "联系我们",
      whatsapp: "WhatsApp 联系我们",
      wechat: "微信: Alvin_Fiji",
      booking: "预约咨询",
      offices: "办公室位于斐济楠迪与苏瓦",
      smallprint: "Alvin Kumar & Associates —— 服务于楠迪与苏瓦的持牌顾问公司。",
      rights: "版权所有。",
    },
  },

  hi: {
    nav: { getGuide: "मुफ़्त गाइड पाएँ" },
    hero: {
      badge: "विदेशी निवेशकों के लिए · नाडी और सुवा",
      line1: "फ़िजी में अपना",
      line2: "व्यवसाय शुरू करें —",
      line3: "लगभग पूरी तरह रिमोट",
      sub: "फ़िजी में शारीरिक रूप से उपस्थित होने की आवश्यकता केवल बैंक खाता खोलने के लिए है। बाकी सब कुछ — कंपनी पंजीकरण, कर, आप्रवासन परमिट — कहीं से भी किया जा सकता है।",
      cta: "मुफ़्त गाइड पाएँ",
      ctaNote: "मुफ़्त चरण-दर-चरण गाइड · कोई बाध्यता नहीं",
      caption: "रिमोट पंजीकरण करें। व्यक्तिगत रूप से बैंकिंग करें। दोनों हम संभालते हैं।",
    },
    why: {
      overline: "यह क्यों मायने रखता है",
      title: "यह प्रक्रिया जानबूझकर उलझाई गई है। ऐसा होना ज़रूरी नहीं।",
      body: "अस्पष्ट लागत। किन हिस्सों में यात्रा चाहिए, इस पर परस्पर विरोधी सलाह। पंजीकरण के बाद हफ़्तों तक अटके रहने का डर। अधिकांश निवेशक शुरू करने से पहले ही हार मान लेते हैं — इसलिए नहीं कि फ़िजी कठिन है, बल्कि इसलिए कि कोई भी पूरा रास्ता एक ही जगह स्पष्ट रूप से नहीं बताता।",
      points: [
        { title: "एक पंजीकृत सलाहकार फर्म", body: "हम एक पंजीकृत सलाहकार फर्म हैं — कोई डायरेक्टरी या लीड ब्रोकर नहीं। आप सीधे उन्हीं लोगों के साथ काम करते हैं जो आपका सेटअप संभालते हैं।" },
        { title: "नाडी और सुवा में कार्यालय", body: "दोनों प्रमुख केंद्रों में स्थानीय उपस्थिति का अर्थ है कि जिस एक चरण में उपस्थिति ज़रूरी है — आपका बैंक खाता — वह स्थानीय स्तर पर संभाला जाता है।" },
        { title: "पंजीकरण से लेकर बैंकिंग तक", body: "हम पूरी श्रृंखला अंत तक संभालते हैं: कंपनी पंजीकरण, कर, परमिट और कॉर्पोरेट बैंक खाता। एक टीम, एक रास्ता।" },
        { title: "हर उद्योग के विदेशी निवेशक", body: "हम हर उद्योग के विदेशी निवेशकों के साथ काम करते हैं — पर्यटन, व्यापार, सेवाएँ, विनिर्माण — केवल किसी एक क्षेत्र तक सीमित नहीं।" },
      ],
    },
    process: {
      overline: "प्रक्रिया",
      title: "फ़िजी में कंपनी स्थापित करने के आठ चरण।",
      sub: "यहाँ चरण और वास्तविक समय-सीमाएँ दी गई हैं। पूरी फीस, दस्तावेज़ चेकलिस्ट और अंदरूनी सुझाव मुफ़्त गाइड में हैं।",
      note: "पूरी फीस, दस्तावेज़ चेकलिस्ट, और वे अंदरूनी सुझाव जिन्हें हम कहीं लिखित में नहीं रखते — नीचे दी गई मुफ़्त गाइड में पाएँ।",
      cta: "मुझे मुफ़्त गाइड भेजें",
      steps: [
        { title: "कंपनी पंजीकरण", time: "2–3 कार्यदिवस" },
        { title: "शेयरधारक व निदेशक TIN पंजीकरण", time: "2–3 कार्यदिवस" },
        { title: "कंपनी कर व VAT पंजीकरण", time: "1 कार्यदिवस" },
        { title: "व्यवसाय प्रोफ़ाइल व MCTTT पंजीकरण", time: "1–3 कार्यदिवस" },
        { title: "रिज़र्व बैंक ऑफ़ फ़िजी विनिमय नियंत्रण अनुमोदन", time: "~3 कार्यदिवस" },
        { title: "निवेशक आप्रवासन परमिट", time: "~21 कार्यदिवस" },
        { title: "कार्य परमिट आवेदन", time: "अलग-अलग" },
        { title: "व्यावसायिक बैंक खाता सेटअप", time: "एकमात्र व्यक्तिगत चरण" },
      ],
    },
    marquee: ["फ़िजी में व्यवसाय शुरू करें", "विदेशी कंपनी पंजीकरण", "फ़िजी निवेशक परमिट", "रिमोट कंपनी सेटअप", "विनिमय नियंत्रण अनुमोदन", "कॉर्पोरेट बैंकिंग"],
    form: {
      overline: "मुफ़्त गाइड",
      title: "फ़िजी में व्यवसाय कैसे शुरू करें",
      sub: "संपूर्ण चरण-दर-चरण गाइड — साथ ही वे अंदरूनी सुझाव जो कोई नहीं बताता। फीस, दस्तावेज़ चेकलिस्ट, समय-सीमाएँ और बचने योग्य जाल। सीधे आपके इनबॉक्स में।",
      name: "पूरा नाम", namePh: "आपका नाम",
      email: "ईमेल पता", emailPh: "you@company.com",
      phone: "फ़ोन नंबर (देश कोड सहित)", phonePh: "+91 98765 43210",
      submit: "मुझे मुफ़्त गाइड भेजें", sending: "भेजा जा रहा है…",
      reassurance: "कोई स्पैम नहीं। हम केवल आपके फ़िजी व्यवसाय सेटअप के बारे में संपर्क करेंगे।",
      successTitle: "अपना इनबॉक्स देखें।",
      successPre: "आपकी मुफ़्त गाइड PDF के रूप में इस पते पर भेजी जा रही है:",
      successPost: "। यदि कुछ मिनटों में न दिखे, तो अपना स्पैम फ़ोल्डर देखें। हम केवल आपके फ़िजी व्यवसाय सेटअप के बारे में संपर्क करेंगे।",
      errName: "कृपया अपना पूरा नाम दर्ज करें।",
      errEmail: "कृपया एक मान्य ईमेल पता दर्ज करें।",
      errPhone: "कृपया देश कोड सहित अपना फ़ोन नंबर दर्ज करें।",
      errorToast: "गाइड भेजने में कुछ गड़बड़ी हुई। कृपया पुनः प्रयास करें।",
    },
    faq: {
      overline: "सामान्य प्रश्न",
      title: "अक्सर पूछे जाने वाले प्रश्न",
      sub: "रिमोट सेटअप, समय-सीमा, स्थानीय निदेशक और निवेश आवश्यकताओं पर सीधे उत्तर।",
      items: [
        { q: "क्या मैं फ़िजी की यात्रा किए बिना व्यवसाय शुरू कर सकता हूँ?", a: "हाँ, कॉर्पोरेट बैंक खाता खोलने को छोड़कर हर चरण रिमोट पूरा किया जा सकता है, जिसमें कंपनी पंजीकरण, कर पंजीकरण, विनिमय नियंत्रण अनुमोदन और आप्रवासन परमिट आवेदन जमा करना शामिल है।" },
        { q: "फ़िजी में कंपनी पंजीकृत करने में कितना समय लगता है?", a: "कंपनी पंजीकरण में आमतौर पर 2–3 कार्यदिवस लगते हैं; परमिट और बैंकिंग सहित पूरी प्रक्रिया कई सप्ताह ले सकती है, जो निवेशक आप्रवासन परमिट की समय-सीमा (लगभग 21 कार्यदिवस) पर निर्भर करती है।" },
        { q: "क्या विदेशी निवेशकों को फ़िजी में स्थानीय निदेशक की आवश्यकता है?", a: "फ़िजी का कंपनी कानून एक स्थानीय निदेशक की माँग करता है; इसे किसी स्थानीय परिचित को नियुक्त करके या स्थानीय निदेशक सेवा का उपयोग करके पूरा किया जा सकता है।" },
        { q: "क्या फ़िजी में विदेशी स्वामित्व वाली कंपनियों के लिए न्यूनतम निवेश की आवश्यकता है?", a: "विदेशी स्वामित्व वाली कंपनियों के लिए एक वैधानिक न्यूनतम निवेश आवश्यकता है, हालाँकि व्यवसाय की प्रकृति के आधार पर कभी-कभी छूट के लिए आवेदन किया जा सकता है।" },
        { q: "विदेशी के रूप में फ़िजी में कंपनी पंजीकृत करने के लिए मुझे कौन-से दस्तावेज़ चाहिए?", a: "सभी शेयरधारकों और निदेशकों के लिए पासपोर्ट या ड्राइविंग लाइसेंस और जन्म प्रमाणपत्र की प्रमाणित प्रतियाँ आवश्यक हैं; निवेशक परमिट आवेदनों के लिए अतिरिक्त दस्तावेज़ (पुलिस क्लियरेंस, मेडिकल रिपोर्ट, व्यवसाय योजना, वित्तीय विवरण) आवश्यक हैं।" },
      ],
    },
    locations: { overline: "हमारे कार्यालय", title: "नाडी और सुवा में हमें खोजें", nadi: "नाडी कार्यालय", suva: "सुवा कार्यालय", directions: "दिशा-निर्देश पाएँ" },
    footer: {
      blurb: "विदेशी निवेशकों को फ़िजी में व्यवसाय शुरू करने में मदद — कंपनी पंजीकरण से लेकर बैंकिंग तक, नाडी और सुवा स्थित हमारे कार्यालयों से अंत तक संभाला गया।",
      cta: "मुफ़्त गाइड पाएँ",
      getInTouch: "संपर्क करें",
      whatsapp: "WhatsApp पर संपर्क करें",
      wechat: "WeChat: Alvin_Fiji",
      booking: "परामर्श बुक करें",
      offices: "नाडी और सुवा, फ़िजी में कार्यालय",
      smallprint: "Alvin Kumar & Associates — नाडी और सुवा की सेवा करने वाली पंजीकृत सलाहकार फर्म।",
      rights: "सर्वाधिकार सुरक्षित।",
    },
  },

  ja: {
    nav: { getGuide: "無料ガイドを入手" },
    hero: {
      badge: "外国人投資家向け · ナンディ＆スバ",
      line1: "フィジーでの",
      line2: "ビジネス設立 —",
      line3: "ほぼ全てリモートで",
      sub: "フィジーに実際に足を運ぶ必要があるのは銀行口座の開設だけです。会社登記、税務、移民許可など、それ以外はすべてどこからでも手続きできます。",
      cta: "無料ガイドを入手",
      ctaNote: "無料のステップバイステップガイド · 義務なし",
      caption: "登記はリモート、口座開設は現地で。両方を当社が代行します。",
    },
    why: {
      overline: "なぜ重要か",
      title: "この手続きは意図的に分かりにくくされています。本来そうである必要はありません。",
      body: "不透明な費用。どの工程に渡航が必要かについての矛盾した助言。登記後に数週間停滞する不安。多くの投資家は始める前に諦めてしまいます — フィジーが難しいからではなく、全体像を一か所で示してくれる人がいないからです。",
      points: [
        { title: "登録済みのアドバイザリー会社", body: "当社は登録済みのアドバイザリー会社です — ディレクトリでも仲介業者でもありません。実際に手続きを担当する担当者と直接やり取りできます。" },
        { title: "ナンディとスバに拠点", body: "両主要都市に現地拠点があるため、唯一現地対応が必要な工程 — 銀行口座開設 — を当社が現地で対応します。" },
        { title: "登記から口座開設まで一貫対応", body: "会社登記、税務、各種許可、法人銀行口座まで、一連の流れを最後まで担当します。ひとつのチーム、ひとつの道筋。" },
        { title: "あらゆる業種の外国人投資家に対応", body: "観光、貿易、サービス、製造など、あらゆる業種の外国人投資家に対応します — 特定の分野に限定しません。" },
      ],
    },
    process: {
      overline: "手続きの流れ",
      title: "フィジーで会社を設立する8つのステップ。",
      sub: "各段階と現実的な所要期間をご紹介します。詳しい費用、必要書類のチェックリスト、内部のコツは無料ガイドに掲載しています。",
      note: "詳しい費用、必要書類チェックリスト、そしてどこにも書き残さない内部のコツ — すべて下記の無料ガイドで入手できます。",
      cta: "無料ガイドを送ってもらう",
      steps: [
        { title: "会社登記", time: "2〜3営業日" },
        { title: "株主・取締役のTIN登録", time: "2〜3営業日" },
        { title: "法人税・VAT登録", time: "1営業日" },
        { title: "事業プロフィール・MCTTT登録", time: "1〜3営業日" },
        { title: "フィジー準備銀行 外国為替管理承認", time: "約3営業日" },
        { title: "投資家移民許可", time: "約21営業日" },
        { title: "就労許可申請", time: "場合による" },
        { title: "法人銀行口座の開設", time: "唯一の現地対応工程" },
      ],
    },
    marquee: ["フィジーで起業", "外資企業の登記", "フィジー投資家許可", "リモートでの会社設立", "外国為替管理承認", "法人バンキング"],
    form: {
      overline: "無料ガイド",
      title: "フィジーでのビジネス設立方法",
      sub: "完全なステップバイステップガイド — さらに誰も教えてくれない内部のコツ。費用、書類チェックリスト、期間、避けるべき落とし穴。あなたの受信箱に直接お届けします。",
      name: "氏名", namePh: "山田 太郎",
      email: "メールアドレス", emailPh: "you@company.com",
      phone: "電話番号（国番号付き）", phonePh: "+81 90 1234 5678",
      submit: "無料ガイドを送ってもらう", sending: "送信中…",
      reassurance: "スパムはありません。フィジーでの事業設立に関するご連絡のみ行います。",
      successTitle: "受信箱をご確認ください。",
      successPre: "無料ガイド（PDF）を次の宛先へ送信中です：",
      successPost: "。数分経っても届かない場合は迷惑メールフォルダをご確認ください。フィジーでの事業設立に関するご連絡のみ行います。",
      errName: "氏名を入力してください。",
      errEmail: "有効なメールアドレスを入力してください。",
      errPhone: "国番号付きの電話番号を入力してください。",
      errorToast: "ガイドの送信中に問題が発生しました。もう一度お試しください。",
    },
    faq: {
      overline: "よくある質問",
      title: "よくあるご質問",
      sub: "リモート設立、所要期間、現地取締役、投資要件についての率直な回答。",
      items: [
        { q: "フィジーに渡航せずにビジネスを設立できますか？", a: "はい、法人銀行口座の開設を除くすべての工程はリモートで完了できます。会社登記、税務登録、外国為替管理承認、移民許可申請の提出が含まれます。" },
        { q: "フィジーで会社を登記するのにどのくらいかかりますか？", a: "会社登記自体は通常2〜3営業日です。許可や口座開設を含む全工程は、投資家移民許可の所要期間（約21営業日）によって数週間かかることがあります。" },
        { q: "外国人投資家はフィジーで現地取締役が必要ですか？", a: "フィジーの会社法では現地取締役が必要です。現地の知人を任命するか、現地取締役サービスを利用することで満たせます。" },
        { q: "フィジーの外資系企業に最低投資額の要件はありますか？", a: "外資系企業には法定の最低投資額要件がありますが、事業の性質によっては免除を申請できる場合があります。" },
        { q: "外国人としてフィジーで会社を登記するにはどの書類が必要ですか？", a: "すべての株主・取締役について、パスポートまたは運転免許証と出生証明書の認証済みコピーが必要です。投資家許可申請には追加書類（無犯罪証明、健康診断書、事業計画、財務資料）が必要です。" },
      ],
    },
    locations: { overline: "オフィス", title: "ナンディ＆スバでお会いしましょう", nadi: "ナンディ オフィス", suva: "スバ オフィス", directions: "経路を表示" },
    footer: {
      blurb: "外国人投資家のフィジーでの起業を支援 — 会社登記から銀行口座開設まで、ナンディとスバのオフィスから一貫して対応します。",
      cta: "無料ガイドを入手",
      getInTouch: "お問い合わせ",
      whatsapp: "WhatsAppで連絡",
      wechat: "WeChat: Alvin_Fiji",
      booking: "相談を予約",
      offices: "フィジー・ナンディ＆スバにオフィス",
      smallprint: "Alvin Kumar & Associates — ナンディとスバに対応する登録済みアドバイザリー会社。",
      rights: "無断複写・転載を禁じます。",
    },
  },

  ko: {
    nav: { getGuide: "무료 가이드 받기" },
    hero: {
      badge: "외국인 투자자를 위한 · 난디 & 수바",
      line1: "피지에서 사업을",
      line2: "시작하세요 —",
      line3: "거의 전부 원격으로",
      sub: "피지에 직접 방문해야 하는 유일한 단계는 은행 계좌 개설입니다. 회사 등록, 세무, 이민 허가 등 나머지는 모두 어디서든 처리할 수 있습니다.",
      cta: "무료 가이드 받기",
      ctaNote: "무료 단계별 가이드 · 의무 없음",
      caption: "등록은 원격으로, 계좌 개설은 직접. 둘 다 저희가 대행합니다.",
    },
    why: {
      overline: "왜 중요한가",
      title: "이 절차는 일부러 복잡하게 되어 있습니다. 꼭 그럴 필요는 없습니다.",
      body: "불투명한 비용. 어떤 단계에 방문이 필요한지에 대한 상충된 조언. 등록 후 몇 주간 지연될 것이라는 두려움. 많은 투자자가 시작도 전에 포기합니다 — 피지가 어려워서가 아니라, 전체 경로를 한곳에 정리해 주는 사람이 없기 때문입니다.",
      points: [
        { title: "등록된 자문 회사", body: "저희는 등록된 자문 회사입니다 — 디렉터리나 중개업체가 아닙니다. 실제로 귀하의 설립을 담당하는 담당자와 직접 협업합니다." },
        { title: "난디와 수바에 사무소", body: "두 주요 거점에 현지 사무소가 있어, 직접 방문이 필요한 유일한 단계 — 은행 계좌 개설 — 을 현지에서 처리합니다." },
        { title: "등록부터 은행 업무까지", body: "회사 등록, 세무, 각종 허가, 법인 은행 계좌까지 전 과정을 끝까지 처리합니다. 하나의 팀, 하나의 경로." },
        { title: "모든 산업의 외국인 투자자", body: "관광, 무역, 서비스, 제조 등 모든 산업의 외국인 투자자와 협업합니다 — 특정 분야에 국한되지 않습니다." },
      ],
    },
    process: {
      overline: "절차",
      title: "피지에서 회사를 설립하는 8단계.",
      sub: "각 단계와 현실적인 소요 기간입니다. 전체 수수료, 서류 체크리스트, 내부 팁은 무료 가이드에 있습니다.",
      note: "전체 수수료, 서류 체크리스트, 그리고 어디에도 문서로 남기지 않는 내부 팁 — 아래 무료 가이드에서 받아보세요.",
      cta: "무료 가이드 보내주세요",
      steps: [
        { title: "회사 등록", time: "2~3 영업일" },
        { title: "주주 및 이사 TIN 등록", time: "2~3 영업일" },
        { title: "법인세 및 VAT 등록", time: "1 영업일" },
        { title: "사업 프로필 및 MCTTT 등록", time: "1~3 영업일" },
        { title: "피지 중앙은행 외환 관리 승인", time: "약 3 영업일" },
        { title: "투자자 이민 허가", time: "약 21 영업일" },
        { title: "취업 허가 신청", time: "경우에 따라 다름" },
        { title: "법인 은행 계좌 개설", time: "유일한 대면 단계" },
      ],
    },
    marquee: ["피지에서 창업", "외국 기업 등록", "피지 투자자 허가", "원격 회사 설립", "외환 관리 승인", "법인 뱅킹"],
    form: {
      overline: "무료 가이드",
      title: "피지에서 사업을 시작하는 방법",
      sub: "완전한 단계별 가이드 — 아무도 알려주지 않는 내부 팁까지. 수수료, 서류 체크리스트, 소요 기간, 피해야 할 함정. 귀하의 받은편지함으로 바로 보내드립니다.",
      name: "성명", namePh: "홍길동",
      email: "이메일 주소", emailPh: "you@company.com",
      phone: "전화번호 (국가번호 포함)", phonePh: "+82 10 1234 5678",
      submit: "무료 가이드 보내주세요", sending: "전송 중…",
      reassurance: "스팸은 없습니다. 피지 사업 설립 관련 문의만 드립니다.",
      successTitle: "받은편지함을 확인하세요.",
      successPre: "무료 가이드(PDF)를 다음 주소로 보내드리고 있습니다:",
      successPost: ". 몇 분 내에 보이지 않으면 스팸 폴더를 확인하세요. 피지 사업 설립 관련 문의만 드립니다.",
      errName: "성명을 입력해 주세요.",
      errEmail: "유효한 이메일 주소를 입력해 주세요.",
      errPhone: "국가번호를 포함한 전화번호를 입력해 주세요.",
      errorToast: "가이드 전송 중 오류가 발생했습니다. 다시 시도해 주세요.",
    },
    faq: {
      overline: "자주 묻는 질문",
      title: "자주 묻는 질문",
      sub: "원격 설립, 소요 기간, 현지 이사, 투자 요건에 대한 명확한 답변.",
      items: [
        { q: "피지에 가지 않고 사업을 설립할 수 있나요?", a: "네, 법인 은행 계좌 개설을 제외한 모든 단계는 원격으로 완료할 수 있습니다. 여기에는 회사 등록, 세무 등록, 외환 관리 승인, 이민 허가 신청 제출이 포함됩니다." },
        { q: "피지에서 회사 등록에 얼마나 걸리나요?", a: "회사 등록 자체는 보통 2~3 영업일이 걸립니다. 허가와 은행 업무를 포함한 전체 과정은 투자자 이민 허가 기간(약 21 영업일)에 따라 몇 주가 걸릴 수 있습니다." },
        { q: "외국인 투자자는 피지에서 현지 이사가 필요한가요?", a: "피지 회사법은 현지 이사를 요구합니다. 현지에 아는 사람을 선임하거나 현지 이사 서비스를 이용하여 충족할 수 있습니다." },
        { q: "피지의 외국인 소유 회사에 최소 투자 요건이 있나요?", a: "외국인 소유 회사에는 법정 최소 투자 요건이 있으나, 사업의 성격에 따라 면제를 신청할 수 있는 경우도 있습니다." },
        { q: "외국인으로서 피지에서 회사를 등록하려면 어떤 서류가 필요한가요?", a: "모든 주주와 이사에 대해 여권 또는 운전면허증과 출생증명서의 공증 사본이 필요합니다. 투자자 허가 신청에는 추가 서류(범죄 경력 증명, 건강 진단서, 사업 계획서, 재무 자료)가 필요합니다." },
      ],
    },
    locations: { overline: "사무소", title: "난디 & 수바에서 만나요", nadi: "난디 사무소", suva: "수바 사무소", directions: "길찾기" },
    footer: {
      blurb: "외국인 투자자의 피지 창업을 지원합니다 — 회사 등록부터 은행 업무까지, 난디와 수바 사무소에서 끝까지 처리합니다.",
      cta: "무료 가이드 받기",
      getInTouch: "문의하기",
      whatsapp: "WhatsApp으로 연락",
      wechat: "WeChat: Alvin_Fiji",
      booking: "상담 예약",
      offices: "피지 난디 & 수바 사무소",
      smallprint: "Alvin Kumar & Associates — 난디와 수바를 담당하는 등록 자문 회사.",
      rights: "모든 권리 보유.",
    },
  },

  es: {
    nav: { getGuide: "Obtener la guía gratis" },
    hero: {
      badge: "Para inversores extranjeros · Nadi y Suva",
      line1: "Establezca su negocio",
      line2: "en Fiyi — casi",
      line3: "totalmente en remoto",
      sub: "El único paso que requiere su presencia física en Fiyi es abrir la cuenta bancaria. Todo lo demás — registro de la empresa, impuestos, permisos de inmigración — puede hacerse desde cualquier lugar.",
      cta: "Obtener la guía gratis",
      ctaNote: "Guía gratuita paso a paso · Sin compromiso",
      caption: "Registre en remoto. Abra la cuenta en persona. Gestionamos ambos.",
    },
    why: {
      overline: "Por qué importa",
      title: "El proceso es confuso a propósito. No tiene por qué serlo.",
      body: "Costes poco claros. Consejos contradictorios sobre qué requiere viajar. El miedo a registrarse y luego quedarse estancado durante semanas. La mayoría de los inversores se rinden antes de empezar — no porque Fiyi sea difícil, sino porque nadie expone el camino completo en un solo lugar.",
      points: [
        { title: "Una firma asesora registrada", body: "Somos una firma asesora registrada — no un directorio ni un intermediario. Trabaja directamente con las personas que gestionan su constitución." },
        { title: "Oficinas en Nadi y Suva", body: "La presencia local en ambos centros principales significa que el único paso que requiere presencia física — su cuenta bancaria — se gestiona localmente." },
        { title: "Del registro a la banca", body: "Gestionamos toda la cadena de principio a fin: registro de la empresa, impuestos, permisos y la cuenta bancaria corporativa. Un solo equipo, un solo camino." },
        { title: "Inversores extranjeros, todos los sectores", body: "Trabajamos con inversores extranjeros de todos los sectores — turismo, comercio, servicios, manufactura — no solo uno." },
      ],
    },
    process: {
      overline: "El proceso",
      title: "Ocho pasos para constituir una empresa en Fiyi.",
      sub: "Estas son las etapas y los plazos realistas. Las tarifas completas, las listas de documentos y los consejos internos están en la guía gratuita.",
      note: "Tarifas completas, listas de documentos y los consejos internos que no ponemos por escrito en ningún otro sitio — obténgalos en la guía gratuita de abajo.",
      cta: "Envíenme la guía gratis",
      steps: [
        { title: "Registro de la empresa", time: "2–3 días hábiles" },
        { title: "Registro del TIN de accionistas y directores", time: "2–3 días hábiles" },
        { title: "Registro de impuestos y VAT de la empresa", time: "1 día hábil" },
        { title: "Perfil de negocio y registro MCTTT", time: "1–3 días hábiles" },
        { title: "Aprobación de control de cambios del Banco de Reserva de Fiyi", time: "~3 días hábiles" },
        { title: "Permisos de inmigración para inversores", time: "~21 días hábiles" },
        { title: "Solicitudes de permiso de trabajo", time: "Variable" },
        { title: "Apertura de cuenta bancaria empresarial", time: "El único paso presencial" },
      ],
    },
    marquee: ["Crear un negocio en Fiyi", "Registro de empresa extranjera", "Permiso de inversor de Fiyi", "Constituir una empresa en remoto", "Aprobación de control de cambios", "Banca corporativa"],
    form: {
      overline: "La guía gratuita",
      title: "Cómo establecer un negocio en Fiyi",
      sub: "La guía completa paso a paso — además de los consejos internos que nadie cuenta. Tarifas, listas de documentos, plazos y las trampas que evitar. Directo a su bandeja de entrada.",
      name: "Nombre completo", namePh: "Juan Pérez",
      email: "Correo electrónico", emailPh: "juan@empresa.com",
      phone: "Teléfono (con código de país)", phonePh: "+34 600 123 456",
      submit: "Envíenme la guía gratis", sending: "Enviando…",
      reassurance: "Sin spam. Solo le contactaremos sobre su constitución en Fiyi.",
      successTitle: "Revise su bandeja de entrada.",
      successPre: "Su guía gratuita va en camino en PDF a",
      successPost: ". Si no la ve en unos minutos, revise su carpeta de spam. Solo le contactaremos sobre su constitución en Fiyi.",
      errName: "Introduzca su nombre completo.",
      errEmail: "Introduzca un correo electrónico válido.",
      errPhone: "Introduzca su teléfono con el código de país.",
      errorToast: "Algo salió mal al enviar su guía. Inténtelo de nuevo.",
    },
    faq: {
      overline: "Preguntas frecuentes",
      title: "Preguntas frecuentes",
      sub: "Respuestas directas sobre la constitución en remoto, los plazos, los directores locales y los requisitos de inversión.",
      items: [
        { q: "¿Puedo establecer un negocio en Fiyi sin viajar allí?", a: "Sí, todos los pasos excepto abrir la cuenta bancaria corporativa pueden completarse en remoto, incluidos el registro de la empresa, el registro fiscal, la aprobación de control de cambios y la presentación de solicitudes de permiso de inmigración." },
        { q: "¿Cuánto se tarda en registrar una empresa en Fiyi?", a: "El registro de la empresa en sí suele tardar 2-3 días hábiles; el proceso completo, incluidos permisos y banca, puede llevar varias semanas según el plazo del permiso de inmigración de inversor (unos 21 días hábiles)." },
        { q: "¿Los inversores extranjeros necesitan un director local en Fiyi?", a: "La ley de sociedades de Fiyi exige un director local; esto puede cumplirse nombrando a alguien que conozca localmente o utilizando un servicio de director local." },
        { q: "¿Existe un requisito de inversión mínima para empresas de propiedad extranjera en Fiyi?", a: "Existe un requisito legal de inversión mínima para empresas de propiedad extranjera, aunque a veces puede solicitarse una exención según la naturaleza del negocio." },
        { q: "¿Qué documentos necesito para registrar una empresa en Fiyi como extranjero?", a: "Se requieren copias certificadas del pasaporte o carné de conducir y del certificado de nacimiento de todos los accionistas y directores, junto con documentos adicionales (antecedentes penales, informe médico, plan de negocio, estados financieros) para las solicitudes de permiso de inversor." },
      ],
    },
    locations: { overline: "Nuestras oficinas", title: "Encuéntrenos en Nadi y Suva", nadi: "Oficina de Nadi", suva: "Oficina de Suva", directions: "Cómo llegar" },
    footer: {
      blurb: "Ayudamos a inversores extranjeros a crear un negocio en Fiyi — del registro de la empresa a la banca, gestionado de principio a fin desde nuestras oficinas de Nadi y Suva.",
      cta: "Obtener la guía gratis",
      getInTouch: "Contacto",
      whatsapp: "Escríbanos por WhatsApp",
      wechat: "WeChat: Alvin_Fiji",
      booking: "Reservar una consulta",
      offices: "Oficinas en Nadi y Suva, Fiyi",
      smallprint: "Alvin Kumar & Associates — Firma asesora registrada al servicio de Nadi y Suva.",
      rights: "Todos los derechos reservados.",
    },
  },

  fr: {
    nav: { getGuide: "Obtenir le guide gratuit" },
    hero: {
      badge: "Pour les investisseurs étrangers · Nadi & Suva",
      line1: "Créez votre entreprise",
      line2: "aux Fidji — presque",
      line3: "entièrement à distance",
      sub: "La seule étape qui exige votre présence physique aux Fidji est l'ouverture de votre compte bancaire. Tout le reste — immatriculation de la société, fiscalité, permis d'immigration — peut se faire de n'importe où.",
      cta: "Obtenir le guide gratuit",
      ctaNote: "Guide gratuit étape par étape · Sans engagement",
      caption: "Immatriculez à distance. Ouvrez le compte sur place. Nous gérons les deux.",
    },
    why: {
      overline: "Pourquoi c'est important",
      title: "Le processus est volontairement déroutant. Il n'a pas à l'être.",
      body: "Des coûts flous. Des conseils contradictoires sur ce qui nécessite un déplacement. La peur de s'immatriculer puis de stagner pendant des semaines. La plupart des investisseurs abandonnent avant de commencer — non parce que les Fidji sont difficiles, mais parce que personne ne présente le parcours complet au même endroit.",
      points: [
        { title: "Un cabinet de conseil enregistré", body: "Nous sommes un cabinet de conseil enregistré — ni un annuaire ni un intermédiaire. Vous travaillez directement avec les personnes qui gèrent votre création." },
        { title: "Des bureaux à Nadi et Suva", body: "Une présence locale dans les deux grands pôles signifie que la seule étape nécessitant une présence physique — votre compte bancaire — est gérée sur place." },
        { title: "De l'immatriculation à la banque", body: "Nous gérons toute la chaîne de bout en bout : immatriculation, fiscalité, permis et compte bancaire professionnel. Une seule équipe, un seul parcours." },
        { title: "Investisseurs étrangers, tous secteurs", body: "Nous travaillons avec des investisseurs étrangers de tous les secteurs — tourisme, commerce, services, industrie — pas seulement un seul." },
      ],
    },
    process: {
      overline: "Le processus",
      title: "Huit étapes pour créer une société aux Fidji.",
      sub: "Voici les étapes et les délais réalistes. Les frais complets, les listes de documents et les conseils d'initiés sont dans le guide gratuit.",
      note: "Frais complets, listes de documents et les conseils d'initiés que nous ne mettons par écrit nulle part ailleurs — obtenez-les dans le guide gratuit ci-dessous.",
      cta: "Envoyez-moi le guide gratuit",
      steps: [
        { title: "Immatriculation de la société", time: "2–3 jours ouvrés" },
        { title: "Enregistrement TIN des actionnaires et dirigeants", time: "2–3 jours ouvrés" },
        { title: "Enregistrement fiscal et VAT de la société", time: "1 jour ouvré" },
        { title: "Profil d'entreprise et enregistrement MCTTT", time: "1–3 jours ouvrés" },
        { title: "Approbation du contrôle des changes de la Banque de réserve des Fidji", time: "~3 jours ouvrés" },
        { title: "Permis d'immigration pour investisseurs", time: "~21 jours ouvrés" },
        { title: "Demandes de permis de travail", time: "Variable" },
        { title: "Ouverture du compte bancaire professionnel", time: "La seule étape en présentiel" },
      ],
    },
    marquee: ["Créer une entreprise aux Fidji", "Immatriculation de société étrangère", "Permis d'investisseur aux Fidji", "Créer une société à distance", "Approbation du contrôle des changes", "Banque d'entreprise"],
    form: {
      overline: "Le guide gratuit",
      title: "Comment créer une entreprise aux Fidji",
      sub: "Le guide complet étape par étape — plus les conseils d'initiés que personne ne vous donne. Frais, listes de documents, délais et pièges à éviter. Directement dans votre boîte de réception.",
      name: "Nom complet", namePh: "Jean Dupont",
      email: "Adresse e-mail", emailPh: "jean@entreprise.com",
      phone: "Numéro de téléphone (avec indicatif)", phonePh: "+33 6 12 34 56 78",
      submit: "Envoyez-moi le guide gratuit", sending: "Envoi…",
      reassurance: "Pas de spam. Nous ne vous contacterons qu'au sujet de votre création d'entreprise aux Fidji.",
      successTitle: "Vérifiez votre boîte de réception.",
      successPre: "Votre guide gratuit est en route au format PDF vers",
      successPost: ". Si vous ne le voyez pas d'ici quelques minutes, vérifiez votre dossier spam. Nous ne vous contacterons qu'au sujet de votre création d'entreprise aux Fidji.",
      errName: "Veuillez saisir votre nom complet.",
      errEmail: "Veuillez saisir une adresse e-mail valide.",
      errPhone: "Veuillez saisir votre numéro de téléphone avec l'indicatif.",
      errorToast: "Une erreur est survenue lors de l'envoi de votre guide. Veuillez réessayer.",
    },
    faq: {
      overline: "FAQ",
      title: "Questions fréquentes",
      sub: "Des réponses claires sur la création à distance, les délais, les dirigeants locaux et les exigences d'investissement.",
      items: [
        { q: "Puis-je créer une entreprise aux Fidji sans m'y rendre ?", a: "Oui, toutes les étapes sauf l'ouverture du compte bancaire professionnel peuvent se faire à distance, y compris l'immatriculation de la société, l'enregistrement fiscal, l'approbation du contrôle des changes et le dépôt des demandes de permis d'immigration." },
        { q: "Combien de temps faut-il pour immatriculer une société aux Fidji ?", a: "L'immatriculation elle-même prend généralement 2 à 3 jours ouvrés ; le processus complet, permis et banque compris, peut prendre plusieurs semaines selon le délai du permis d'immigration pour investisseur (environ 21 jours ouvrés)." },
        { q: "Les investisseurs étrangers ont-ils besoin d'un dirigeant local aux Fidji ?", a: "Le droit des sociétés fidjien exige un dirigeant local ; cela peut être satisfait en nommant une personne que vous connaissez sur place ou en recourant à un service de dirigeant local." },
        { q: "Existe-t-il un investissement minimum pour les sociétés à capitaux étrangers aux Fidji ?", a: "Il existe une exigence légale d'investissement minimum pour les sociétés à capitaux étrangers, bien qu'une dérogation puisse parfois être demandée selon la nature de l'activité." },
        { q: "Quels documents faut-il pour immatriculer une société aux Fidji en tant qu'étranger ?", a: "Des copies certifiées du passeport ou du permis de conduire et de l'acte de naissance sont requises pour tous les actionnaires et dirigeants, avec des documents supplémentaires (casier judiciaire, rapport médical, plan d'affaires, états financiers) pour les demandes de permis d'investisseur." },
      ],
    },
    locations: { overline: "Nos bureaux", title: "Trouvez-nous à Nadi & Suva", nadi: "Bureau de Nadi", suva: "Bureau de Suva", directions: "Itinéraire" },
    footer: {
      blurb: "Nous aidons les investisseurs étrangers à créer une entreprise aux Fidji — de l'immatriculation à la banque, géré de bout en bout depuis nos bureaux de Nadi et Suva.",
      cta: "Obtenir le guide gratuit",
      getInTouch: "Nous contacter",
      whatsapp: "Écrivez-nous sur WhatsApp",
      wechat: "WeChat : Alvin_Fiji",
      booking: "Réserver une consultation",
      offices: "Bureaux à Nadi & Suva, Fidji",
      smallprint: "Alvin Kumar & Associates — Cabinet de conseil enregistré au service de Nadi et Suva.",
      rights: "Tous droits réservés.",
    },
  },

  ar: {
    nav: { getGuide: "احصل على الدليل المجاني" },
    hero: {
      badge: "للمستثمرين الأجانب · نادي وسوفا",
      line1: "أسّس عملك",
      line2: "في فيجي — بشكل",
      line3: "شبه كامل عن بُعد",
      sub: "الخطوة الوحيدة التي تتطلب حضورك شخصيًا في فيجي هي فتح الحساب البنكي. أما كل ما عداها — تسجيل الشركة والضرائب وتصاريح الهجرة — فيمكن إنجازه من أي مكان.",
      cta: "احصل على الدليل المجاني",
      ctaNote: "دليل مجاني خطوة بخطوة · دون أي التزام",
      caption: "سجّل عن بُعد، وافتح الحساب حضوريًا. نتولى كليهما نيابةً عنك.",
    },
    why: {
      overline: "لماذا يهمّ هذا",
      title: "العملية معقّدة عن قصد. لكنها ليست مضطرة لأن تكون كذلك.",
      body: "تكاليف غير واضحة. نصائح متضاربة حول ما يتطلب السفر. الخوف من التسجيل ثم التعطّل لأسابيع. يستسلم معظم المستثمرين قبل أن يبدأوا — ليس لأن فيجي صعبة، بل لأن لا أحد يعرض المسار الكامل في مكان واحد.",
      points: [
        { title: "شركة استشارية مسجّلة", body: "نحن شركة استشارية مسجّلة — لسنا دليلًا ولا وسيطًا. تتعامل مباشرةً مع من يتولّون تأسيس شركتك." },
        { title: "مكاتب في نادي وسوفا", body: "وجودنا الميداني في المركزين الرئيسيين يعني أن الخطوة الوحيدة التي تتطلب الحضور — حسابك البنكي — تُنجَز محليًا." },
        { title: "من التسجيل حتى الخدمات البنكية", body: "نتولى السلسلة كاملةً من البداية للنهاية: تسجيل الشركة والضرائب والتصاريح والحساب البنكي للشركة. فريق واحد، ومسار واحد." },
        { title: "مستثمرون أجانب في كل القطاعات", body: "نعمل مع مستثمرين أجانب في جميع القطاعات — السياحة والتجارة والخدمات والتصنيع — وليس قطاعًا واحدًا فقط." },
      ],
    },
    process: {
      overline: "العملية",
      title: "ثماني خطوات لتأسيس شركة في فيجي.",
      sub: "إليك المراحل والمدد الزمنية الواقعية. أما الرسوم الكاملة وقوائم المستندات والنصائح الداخلية فتجدها في الدليل المجاني.",
      note: "الرسوم الكاملة وقوائم المستندات والنصائح الداخلية التي لا ندوّنها في أي مكان آخر — احصل عليها في الدليل المجاني أدناه.",
      cta: "أرسل لي الدليل المجاني",
      steps: [
        { title: "تسجيل الشركة", time: "2–3 أيام عمل" },
        { title: "تسجيل رقم TIN للمساهمين والمديرين", time: "2–3 أيام عمل" },
        { title: "تسجيل ضريبة الشركة وضريبة VAT", time: "يوم عمل واحد" },
        { title: "الملف التجاري وتسجيل MCTTT", time: "1–3 أيام عمل" },
        { title: "موافقة الرقابة على النقد من مصرف فيجي الاحتياطي", time: "~3 أيام عمل" },
        { title: "تصاريح هجرة المستثمرين", time: "~21 يوم عمل" },
        { title: "طلبات تصاريح العمل", time: "تختلف" },
        { title: "فتح الحساب البنكي للشركة", time: "الخطوة الحضورية الوحيدة" },
      ],
    },
    marquee: ["ابدأ عملاً في فيجي", "تسجيل شركة أجنبية", "تصريح مستثمر فيجي", "تأسيس شركة عن بُعد", "موافقة الرقابة على النقد", "الخدمات البنكية للشركات"],
    form: {
      overline: "الدليل المجاني",
      title: "كيف تؤسّس عملاً في فيجي",
      sub: "الدليل الكامل خطوة بخطوة — إضافةً إلى النصائح الداخلية التي لا يخبرك بها أحد. الرسوم وقوائم المستندات والمدد والمزالق التي يجب تجنّبها. يصلك مباشرةً إلى بريدك.",
      name: "الاسم الكامل", namePh: "محمد أحمد",
      email: "البريد الإلكتروني", emailPh: "you@company.com",
      phone: "رقم الهاتف (مع رمز الدولة)", phonePh: "+971 50 123 4567",
      submit: "أرسل لي الدليل المجاني", sending: "جارٍ الإرسال…",
      reassurance: "لا رسائل مزعجة. سنتواصل معك فقط بشأن تأسيس عملك في فيجي.",
      successTitle: "تحقّق من بريدك الوارد.",
      successPre: "دليلك المجاني في طريقه بصيغة PDF إلى",
      successPost: ". إن لم تجده خلال دقائق، فتحقّق من مجلد الرسائل المزعجة. سنتواصل معك فقط بشأن تأسيس عملك في فيجي.",
      errName: "يرجى إدخال اسمك الكامل.",
      errEmail: "يرجى إدخال بريد إلكتروني صالح.",
      errPhone: "يرجى إدخال رقم هاتفك مع رمز الدولة.",
      errorToast: "حدث خطأ أثناء إرسال الدليل. يرجى المحاولة مرة أخرى.",
    },
    faq: {
      overline: "الأسئلة الشائعة",
      title: "الأسئلة الشائعة",
      sub: "إجابات مباشرة حول التأسيس عن بُعد والمدد والمدير المحلي ومتطلبات الاستثمار.",
      items: [
        { q: "هل يمكنني تأسيس عمل في فيجي دون السفر إليها؟", a: "نعم، يمكن إنجاز كل الخطوات عن بُعد باستثناء فتح الحساب البنكي للشركة، بما في ذلك تسجيل الشركة والتسجيل الضريبي وموافقة الرقابة على النقد وتقديم طلبات تصاريح الهجرة." },
        { q: "كم يستغرق تسجيل شركة في فيجي؟", a: "يستغرق تسجيل الشركة نفسه عادةً 2–3 أيام عمل؛ أما العملية الكاملة بما فيها التصاريح والخدمات البنكية فقد تستغرق عدة أسابيع حسب مدة تصريح هجرة المستثمر (نحو 21 يوم عمل)." },
        { q: "هل يحتاج المستثمرون الأجانب إلى مدير محلي في فيجي؟", a: "يشترط قانون الشركات الفيجي وجود مدير محلي؛ ويمكن تحقيق ذلك بتعيين شخص تعرفه محليًا أو باستخدام خدمة مدير محلي." },
        { q: "هل هناك حد أدنى للاستثمار للشركات المملوكة لأجانب في فيجي؟", a: "يوجد حد أدنى قانوني للاستثمار للشركات المملوكة لأجانب، إلا أنه يمكن أحيانًا طلب إعفاء بحسب طبيعة النشاط التجاري." },
        { q: "ما المستندات التي أحتاجها لتسجيل شركة في فيجي كأجنبي؟", a: "يلزم تقديم نسخ مصدّقة من جواز السفر أو رخصة القيادة وشهادة الميلاد لجميع المساهمين والمديرين، مع مستندات إضافية (شهادة عدم محكومية، تقرير طبي، خطة عمل، بيانات مالية) لطلبات تصريح المستثمر." },
      ],
    },
    locations: { overline: "مكاتبنا", title: "تجدنا في نادي وسوفا", nadi: "مكتب نادي", suva: "مكتب سوفا", directions: "احصل على الاتجاهات" },
    footer: {
      blurb: "نساعد المستثمرين الأجانب على بدء عمل في فيجي — من تسجيل الشركة حتى الخدمات البنكية، بإدارة كاملة من مكتبينا في نادي وسوفا.",
      cta: "احصل على الدليل المجاني",
      getInTouch: "تواصل معنا",
      whatsapp: "راسلنا عبر WhatsApp",
      wechat: "WeChat: Alvin_Fiji",
      booking: "احجز استشارة",
      offices: "مكاتب في نادي وسوفا، فيجي",
      smallprint: "Alvin Kumar & Associates — شركة استشارية مسجّلة تخدم نادي وسوفا.",
      rights: "جميع الحقوق محفوظة.",
    },
  },
};
